import { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Header } from 'foundations-components/transitional/components/Header';
import { SideNav, SideNavItem } from 'foundations-components/transitional/components/SideNav';
import SetupGuideNavFooter from './SetupGuideNavFooter';
import SetupGuideNavIntegrationsWidget from './SetupGuideNavIntegrationsWidget';
import { useHighestProductLevelEditions } from 'setup-guide-api/hooks/productLevels';
import { useTabViews, useSetSelectedView, useViewsOrder } from '../../hooks/config';
import { getViewTitle } from '../../utils/views';
import { trackTabView, trackClickSideNav } from '../../utils/usageTracker';

/* @bend-expect no-styled-foundations-components */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SideNavContainer = styled(Flex).withConfig({
  displayName: "SetupGuideNav__SideNavContainer",
  componentId: "xhlz1y-0"
})(["hyphens:auto;overflow-wrap:break-word;"]);
const SetupGuideNav = () => {
  const productEditions = useHighestProductLevelEditions();
  const {
    selectedView,
    views
  } = useTabViews() || {};
  // TODO - remove this when useCase is removed from the OnboardingViews
  // https://git.hubteam.com/hubspot/growthonboardingplanningsetup/issues/3953
  const selectedViewEdition = selectedView && productEditions[selectedView];
  const orderedViews = useViewsOrder(views);
  const setSelectedView = useSetSelectedView();
  const onNavItemClick = useCallback((view, groupKey) => {
    trackClickSideNav(view);
    setSelectedView(groupKey, view);
  }, [setSelectedView]);
  useEffect(() => {
    if (selectedView && selectedViewEdition) {
      trackTabView({
        tab: selectedView,
        edition: selectedViewEdition
      });
    }
  }, [selectedView, selectedViewEdition]);
  if (!views) {
    return null;
  }
  return /*#__PURE__*/_jsxs(SideNavContainer, {
    alignItems: "stretch",
    blockSize: "100%",
    flexDirection: "column",
    justifyContent: "space-between",
    children: [/*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      flexGrow: 1,
      gap: "600",
      children: [/*#__PURE__*/_jsx(SideNav, {
        "aria-label": I18n.text('app.tabs.title'),
        "data-test-id": "user-guide-nav",
        headingText: /*#__PURE__*/_jsx(Header, {
          "data-test-id": "app-header",
          paddingBlockEnd: "0",
          paddingBlockStart: "600",
          paddingInline: "0",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.tabs.title"
          }),
          use: "condensed"
        }),
        value: "",
        children: orderedViews.map(view => {
          const viewSettings = views[view];
          const isActive = view === selectedView;
          const productLevel = productEditions[view];
          return /*#__PURE__*/_jsx(SideNavItem, {
            active: isActive,
            "aria-current": isActive,
            "data-product-level": productLevel,
            "data-tab-id": view,
            "data-tab-selected": isActive,
            "data-test-id": `user-guide-nav-item-${view}`,
            onClick: () => {
              onNavItemClick(view, viewSettings.groupKey || undefined);
            },
            title: getViewTitle(view)
          }, view);
        })
      }), /*#__PURE__*/_jsx(SetupGuideNavIntegrationsWidget, {})]
    }), /*#__PURE__*/_jsx(SetupGuideNavFooter, {})]
  });
};
export default SetupGuideNav;